import * as React from 'react'
import { forwardRef, Ref, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppService, DomainIntegrationService } from '../../services'
import {
    Background,
    BackgroundVariant,
    Controls,
    Panel,
    ReactFlow,
    ReactFlowProps,
    ReactFlowProvider,
    useEdgesState,
    useNodesState,
} from '@xyflow/react'
import { Drawer, Well } from '@pushly/aqe/lib/components'
import { CustomNode } from './node-types/custom-node/custom-node'
import { CustomEdge } from './edge-types/custom-edge/custom-edge'
import { NodeEditor } from './editors/node-editor'
import { JourneyBuilderMode } from './enums'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { DomainDto } from '../../dtos/domain'
import { JourneyContext } from './context'
import { useJourneyReducer } from './reducer'
import titleCase from 'title-case'
import { getClassNames } from '../../_utils/classnames'
import { buildInitialJourneyFlow, parseJourneyFromFlow, positionNodes } from './helpers'
import { getEditableState } from '../../components/campaign-builder/helpers/campaign'
import { CampaignEditableState } from '../../components/campaign-builder/enums'
import { Journey } from './types/journey-api-response'
import { JourneyEdge, JourneyNode, JourneyStep, NodeEditorRef } from './types/journey-nodes'
import { CopyStates, JourneyAction, JourneyState } from './types/journey-context'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import { GetDomainIntegrationResponse } from '@pushly/models/lib/structs/domains'
import './styles/journey.scss'
import { StatusType } from '../../enums/status-type'
import { RemoveStepConfirmationModal } from './modals/remove-step-confirmation-modal'
import { JourneyStatusRef } from '../campaigns/types'

type JourneyBuilderProps = {
    ref?: Ref<JourneyStatusRef>
    loading?: boolean
    domain: DomainDto
    journey: Journey
    domainIntegrations?: GetDomainIntegrationResponse[]
    onChange?: (journey: any) => void
    onSubmit?: (journey: Journey, remove?: boolean) => void
    mode?: JourneyBuilderMode
    flowOverrides?: ReactFlowProps<JourneyNode, JourneyEdge>
}

export const JourneysBuilder = forwardRef<JourneyStatusRef, JourneyBuilderProps>(
    ({ loading, domain, journey, domainIntegrations, mode, onChange, onSubmit, flowOverrides }, ref) => {
        // region Builder Definitions
        const appSvc = useService(AppService)
        const domainIntegrationSvc = useService(DomainIntegrationService)

        const [loadingDependencies, setLoadingDependencies] = React.useState(false)
        const [nodes, setNodes, onNodesChange] = useNodesState<JourneyNode>([])
        const [edges, setEdges, onEdgesChange] = useEdgesState<JourneyEdge>([])
        const [removedNodes, setRemovedNodes] = React.useState<JourneyNode[]>([])
        const [removeModalOpen, setRemoveModalOpen] = React.useState(false)

        const [submitEnabled, setSubmitEnabled] = React.useState(false)

        const nodeRef = useRef<NodeEditorRef>(null)
        const builderRef = useRef<{ builderHasChanged: boolean }>({ builderHasChanged: false })

        // Nodes are rendered from these types passed to ReactFlow StepTypes prop
        // node.type === StepTypes[node.type] determines render component
        const StepTypes = useMemo(
            () => ({
                custom: CustomNode,
            }),
            [],
        )

        // Edges are rendered the these types passed to ReactFlow.edgeTypes
        // edge.type === edgeTypes[node.type] determines render component
        const edgeTypes = useMemo(
            () => ({
                custom: CustomEdge,
            }),
            [],
        )
        // endregion

        // region Node Handlers
        // --------------- Node Interactions -------------------- //
        // setup node interactivity handlers/callbacks to register to context
        const handleNodeAdd = (node: JourneyNode) => {
            if (node.data.step.type !== StepType.EXIT && node.data.step.type !== StepType.TRIGGER) {
                dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: node })
            }
        }

        const handleNodeUpdate = <T extends JourneyStep>(node: JourneyNode<T>, nodeUpdate: JourneyNode<T>) => {
            setNodes((prev) =>
                prev.map((n) => {
                    if (n.id === node.id) {
                        return nodeUpdate
                    }

                    return n
                }),
            )

            return nodes
        }

        const handleNodeRemove = (node: JourneyNode) => {
            if (node.data.step.type !== StepType.EXIT && node.data.step.type !== StepType.TRIGGER) {
                setRemovedNodes((prev) => [...prev, node])
            }
        }

        // local handlers

        const handleNodeClick = useCallback(
            (ev: React.MouseEvent<HTMLDivElement, MouseEvent>, node: JourneyNode) => {
                dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: node })
            },
            [nodes],
        )

        const handleCloseEditor = () => {
            dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: null })
        }

        const handleSubmitJourney = () => {
            const journeyUpdate = parseJourneyFromFlow(state)

            if (
                submitEnabled &&
                mode !== JourneyBuilderMode.CREATE &&
                // @todo NS-1663 - update to Journey types/helpers
                getEditableState(journey) === CampaignEditableState.RUNNING &&
                removedNodes.length > 0
            ) {
                setRemoveModalOpen(true)
            } else {
                onSubmit?.(journeyUpdate)
            }
        }

        // endregion

        const emitChanges = (journeyState: JourneyState) => {
            onChange?.(parseJourneyFromFlow(journeyState))
        }

        // initialize journey context
        const [state, dispatch] = useJourneyReducer({
            domain,
            journey: structuredClone(journey),
            mode: mode ?? JourneyBuilderMode.READONLY,
            callbacks: {
                onNodeAdd: handleNodeAdd,
                onNodeUpdate: handleNodeUpdate,
                handleNodeRemove,
                onNodeClick: handleNodeClick,
                emitChanges,
            },
            nodes,
            edges,
            copy: { state: CopyStates.CLEAR, node: null },
            editorOpen: false,
        })

        // region Builder Effects
        // ------------------------- Effects ------------------------ //

        // all effects will trigger context update separate from flow update handling
        // maintain this pattern for better rendering/memoization performance
        useEffect(() => {
            if (nodes?.length && edges?.length) {
                // Apply auto-layout
                const updatedNodes = positionNodes(nodes)

                dispatch({ action: JourneyAction.ON_JOURNEY_CHANGES, data: { nodes: updatedNodes, edges } })
            }
        }, [nodes, edges, setNodes, setEdges])

        // on mount build journey flow, dispatch to journey context initial states
        useEffect(() => {
            const getDomainIntegrations = async () => {
                setLoadingDependencies(true)
                const response = await domainIntegrationSvc.fetchAll(domain.id)
                let integrationMap: Map<number, GetDomainIntegrationResponse> = new Map()

                if (response.ok && response.data?.length) {
                    integrationMap = new Map(
                        response.data.map((int) => [int.id, GetDomainIntegrationResponse.from(int)]),
                    )
                    dispatch({
                        action: JourneyAction.SET_JOURNEY_INTEGRATIONS,
                        data: integrationMap,
                    })
                }

                setLoadingDependencies(false)
                return integrationMap
            }

            const buildInitialJourneyNodesAndEdges = async () => {
                const integrations = domainIntegrations?.length
                    ? new Map(domainIntegrations.map((int) => [int.id, GetDomainIntegrationResponse.from(int)]))
                    : await getDomainIntegrations()

                if (journey) {
                    const [initialNodes, initialEdges] = buildInitialJourneyFlow(journey.revision.steps, {
                        integrations,
                    })

                    dispatch({ action: JourneyAction.SET_JOURNEY, data: journey })
                    dispatch({
                        action: JourneyAction.ON_JOURNEY_CHANGES,
                        data: { nodes: initialNodes, edges: initialEdges },
                    })

                    setNodes(initialNodes)
                    setEdges(initialEdges)
                }
            }

            buildInitialJourneyNodesAndEdges()
        }, [journey?.id])
        // endregion

        const detectChanges = () => {
            // not all campaigns include global trigger steps
            const ogSteps = journey.steps.filter((s) => s.type !== StepType.GLOBAL_TRIGGER)
            // skip checking steps | if length changes then campaign def has changed
            if (state.nodes.length !== ogSteps.length) {
                return true
            }

            return state.nodes.some(({ data }) => {
                if (
                    (data.step.type === StepType.ACTION &&
                        data.step.configuration.type === JourneyActionType.SEND_NOTIFICATION) ||
                    data.step.configuration.type === JourneyActionType.SEND_CART_NOTIFICATION ||
                    data.step.configuration.type === JourneyActionType.SEND_RANKED_ITEM_NOTIFICATION
                ) {
                    delete data.step.configuration.params.notification?.template?.channels?.default?.keywords
                }

                return data.hasChanged()
            })
        }

        useEffect(() => {
            if (state.journey?.id) {
                /** submit button is enabled if:
                 * - journey is in draft
                 * - journey is in create mode
                 * - journey is in edit mode and has changed
                 */
                setSubmitEnabled(
                    mode === JourneyBuilderMode.CREATE || journey.status === StatusType.DRAFT.name || detectChanges(),
                )
                builderRef.current.builderHasChanged = detectChanges()
            }
        }, [state.nodes, JSON.stringify(state.journey)])

        const getFitViewNodes = (journeyNodes: JourneyNode[]) => journeyNodes.filter((_n, idx) => idx <= 3)
        const fitViewOptions = {
            nodes: getFitViewNodes(nodes),
        }

        useImperativeHandle(ref, () => ({
            builderHasChanged: builderRef.current.builderHasChanged ?? false,
        }))

        return (
            <Well
                className={getClassNames('journey-builder-well', 'nested')}
                title="Builder"
                onSubmit={handleSubmitJourney}
                submitText="Save"
                disableSubmit={!submitEnabled}
                hideSubmit={mode === JourneyBuilderMode.READONLY}
                hideCancel={true}
                loading={loading || loadingDependencies}
            >
                {!(loading && loadingDependencies) && state.journey && (
                    <JourneyContext.Provider value={{ state, dispatch }}>
                        <ReactFlowProvider>
                            <ReactFlow<JourneyNode, JourneyEdge>
                                className={getClassNames('journey-builder')}
                                nodes={state.nodes}
                                edges={state.edges}
                                nodeTypes={StepTypes}
                                onNodesChange={onNodesChange}
                                nodesConnectable={false}
                                nodesDraggable={false}
                                nodesFocusable={mode !== JourneyBuilderMode.READONLY}
                                edgeTypes={edgeTypes}
                                onEdgesChange={onEdgesChange}
                                edgesReconnectable={false}
                                edgesFocusable={false}
                                fitView={true}
                                maxZoom={1}
                                fitViewOptions={fitViewOptions}
                                zoomOnDoubleClick={false}
                                proOptions={{
                                    hideAttribution: true,
                                }}
                                {...flowOverrides}
                            >
                                {builderRef.current?.builderHasChanged && (
                                    <Panel position="top-right">
                                        <div className="journey-builder-unsaved-changes-panel">
                                            The Campaign currently contains unsaved changes. Please save before
                                            navigating away from this page.
                                        </div>
                                    </Panel>
                                )}
                                <Background
                                    variant={BackgroundVariant.Lines}
                                    bgColor="rgb(242, 242, 242)"
                                    color="rgb(217, 217, 217)"
                                    lineWidth={0.5}
                                    gap={15}
                                />
                                <Controls
                                    position="top-right"
                                    orientation="horizontal"
                                    showInteractive={false}
                                    fitViewOptions={fitViewOptions}
                                />
                            </ReactFlow>
                        </ReactFlowProvider>

                        <Drawer
                            getContainer={appSvc.getAppContainer}
                            className={getClassNames(
                                'journey-node-editor',
                                state.activeNode?.data?.step?.type,
                                state.activeNode?.data?.step?.configuration?.type,
                            )}
                            destroyOnClose={true}
                            title={`${titleCase(
                                state.mode === JourneyBuilderMode.READONLY ? 'View' : state.mode,
                            )} ${titleCase(
                                (state.activeNode?.data.step.type === StepType.ACTION
                                    ? state.activeNode.data.step.configuration.type
                                    : state.activeNode?.data.step.type) ?? '',
                            )}`}
                            placement="right"
                            closable={true}
                            maskClosable={false}
                            open={state.editorOpen}
                            onClose={handleCloseEditor}
                            hideFooter={mode === JourneyBuilderMode.READONLY}
                            submitText="Submit Step"
                            onSubmit={async () => {
                                const nodeChange = await nodeRef?.current?.onSubmit()

                                if (nodeChange) {
                                    dispatch({ action: JourneyAction.ON_NODE_UPDATE, data: { updates: nodeChange } })
                                    dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: null })
                                }
                            }}
                            footer={state.editorFooter}
                        >
                            {state.activeNode && <NodeEditor node={state.activeNode!} ref={nodeRef} />}
                        </Drawer>

                        <RemoveStepConfirmationModal
                            changeSet={{ campaign: parseJourneyFromFlow(state) }}
                            visible={removeModalOpen}
                            onConfirm={(updatedJourney, removeSubs) => {
                                setRemoveModalOpen(false)
                                onSubmit?.(updatedJourney, removeSubs)
                            }}
                            onCancel={() => setRemoveModalOpen(false)}
                        />
                    </JourneyContext.Provider>
                )}
            </Well>
        )
    },
)
